import Vue from "vue";
import { Item } from "./item";
import { removeFromParentList, sortIntoParentList } from "./ordered-relation";
import {
  DATE_UNITS,
  NO_PARENT,
  MULTI,
  ORDRD,
  RELATION_CONFIG,
  REL_NO_PARENT,
  defaultContainerID,
  multiRelations,
  orderedRelations,
  generateDateItem,
  seedItems,
  ITEM_TYPES,
} from "./structure";
import { migrateFromOldFormat, migrationEnabled } from "./migration";

// Originally, I wanted to simply use Vue.observable and a simple object,
// but it turns out that it doesn't give the same reactivity as using
// data attributes on a Vue instance, so I create a full-fledged Vue
// instance to store the reactive values.
const reactiveCache = new Vue({
  data: {
    items: {},
    local: {},
    indexes: {},
    defaultDB: null,
  },
  methods: {
    get(key) {
      if (!key) return null;
      let item = this.items[key];
      if (!item && DATE_UNITS.includes(key.split(":")[0])) {
        item = generateDateItem(key, this, this.defaultDB);
      }
      return item;
    },
    set(item) {
      Vue.set(this.items, item._id, item);
    },
    remove(key) {
      Vue.delete(this.items, key);
      Vue.delete(this.local, key);
    },
    async init(defaultDB, dbs) {
      this.defaultDB = defaultDB;
      // Create virtual root items.
      // They serve as parents for top-level containers (lists etc.)
      // and allow ordering them using the same mechanism as tasks.
      for (const doc of seedItems(defaultDB)) {
        this.processRecord(doc, defaultDB);
      }

      for (const db of dbs) {
        // load initial data
        const data = await db.allDocs({ include_docs: true, update_seq: true });
        for (const record of data.rows) {
          // The row format is similar enough to the change format that we can pass
          // it directly as a change. The only fields that we read are
          // `id`, `doc` (both properly set in the row), and `deleted` (unset in the
          // row - there are no rows for deleted docs).
          this.processRecord(record, db);
        }

        // watch for updates
        db.changes({
          since: data.update_seq,
          live: true,
          include_docs: true,
        }).on("change", (change) => this.processRecord(change, db));
      }
    },
    getIndex(relation, key) {
      if (!this.indexes[key]) {
        switch (RELATION_CONFIG[relation]) {
          case ORDRD:
            Vue.set(this.indexes, key, []);
            break;
          case MULTI:
          case NO_PARENT:
            Vue.set(this.indexes, key, {});
            break;
        }
      }
      return this.indexes[key];
    },
    processRecord(record, pouchDB) {
      // This function may be fed data from different sources, with different structure:
      // db.changes: {last_seq, results: Array[{changes, doc?, deleted?, id, seq}]}
      // db.allDocs: {offset, rows: Array[{id, key, value, doc?, error?}], total_rows}
      // db.find: {docs: Array[doc]}
      if (record.error) {
        return;
      }

      // compatibility layer for handling various input formats
      const id = record._id ? record._id : record.id;
      const doc = record._id ? record : record.doc;

      // Skip internal couchDB documents.
      if (id[0] === "_") {
        return;
      }

      const considerDeleted = record.deleted || doc.trashed;
      if (!considerDeleted && migrationEnabled) migrateFromOldFormat(doc, pouchDB);

      const oldObj = this.get(id) || null;
      const newObj = considerDeleted ? null : new Item(doc, this, pouchDB);

      for (const relation of orderedRelations) {
        const oldPlacement = oldObj?.[relation];
        const newPlacement = newObj?.[relation];

        if (
          oldObj &&
          newObj &&
          ITEM_TYPES.includes(newObj.type) &&
          !oldPlacement &&
          !newPlacement
        ) {
          // Update object in place.
          const children = this.getIndex(REL_NO_PARENT, defaultContainerID(relation));
          Vue.set(children, id, newObj);
          continue;
        }

        const sameParent =
          oldPlacement && newPlacement && newPlacement.key === oldPlacement.key;
        if (sameParent && newPlacement.seq === oldPlacement.seq) {
          // Update object in place.
          const children = this.getIndex(relation, oldPlacement.key);
          const idx = children.findIndex((child) => child._id === id);
          Vue.set(children, idx, newObj);
          continue;
        }
        if (oldObj) {
          // Remove from the old place.
          if (oldPlacement) {
            const children = this.getIndex(relation, oldPlacement.key);
            removeFromParentList(children, relation, oldObj);
          } else if (ITEM_TYPES.includes(oldObj.type)) {
            const children = this.getIndex(REL_NO_PARENT, defaultContainerID(relation));
            Vue.delete(children, id);
          }
        }
        if (newObj) {
          // Insert into the new place.
          if (newPlacement) {
            const children = this.getIndex(relation, newPlacement.key);
            sortIntoParentList(children, relation, newObj);
          } else if (ITEM_TYPES.includes(newObj.type)) {
            const children = this.getIndex(REL_NO_PARENT, defaultContainerID(relation));
            Vue.set(children, id, newObj);
          }
        }
      }

      for (const relation of multiRelations) {
        if (oldObj?.[relation] || newObj?.[relation]) {
          const oldParents = Object.keys(oldObj?.[relation] || {});
          const newParents = Object.keys(newObj?.[relation] || {});

          for (const parentID of newParents) {
            // Add or update.
            Vue.set(this.getIndex(relation, parentID), id, newObj);
          }

          const removedParents = oldParents.filter((id) => !newParents.includes(id));
          for (const parentID of removedParents) {
            Vue.delete(this.getIndex(relation, parentID), id);
          }
        }
      }

      if (considerDeleted) {
        this.remove(id);
      } else {
        this.set(newObj);
        if (!this.local[id]) {
          // Attempt to load the local data for the item in case it is there
          // but wasn't loaded yet.
          pouchDB.get(newObj.localDataId).then(
            (localValue) => {
              Vue.set(this.local, id, localValue);
            },
            /* Ignore errors. */
            () => {},
          );
        }
      }
    },
  },
});

export default reactiveCache;

export function getItem(key) {
  const item = reactiveCache.get(key);
  if (!item) throw `Item with key ${key} not found`;
  return item;
}

// no error on non-existing keys.
export function maybeGetItem(key) {
  return reactiveCache.get(key);
}

// debugging helpers
window.cache = reactiveCache;
